import React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import { luckBoxSizes, type LuckRecordBoxModel } from '../../lib/hallOfFame'

const SCORE_TONE = { lucky: 'text-olive-up', unlucky: 'text-blood', neutral: 'text-ink' } as const

/**
 * One dice-luck record. The title names the record, so the lucky/unlucky
 * colour is never the only signal. Renders an empty placeholder until someone
 * qualifies.
 */
export function LuckRecordBox({ model, emptyTitle }: { model: LuckRecordBoxModel | null; emptyTitle: string }) {
  const router = useRouter()
  const [contentWidth, setContentWidth] = React.useState(0)
  if (!model) {
    return (
      <View className="flex-1 rounded-lg border border-olive bg-paper-up px-3 py-3 gap-1">
        <Text className="font-stencil text-[10px] uppercase tracking-widest text-ink-soft">{emptyTitle}</Text>
        <Text className="font-typed text-xs text-ink-soft italic">// no record yet</Text>
      </View>
    )
  }
  const tone = model.tone === 'lucky' ? 'text-olive-up' : 'text-blood'
  const sizes = luckBoxSizes(model, contentWidth)
  return (
    <TouchableOpacity
      onPress={() => router.push(model.href)}
      accessibilityRole="button"
      accessibilityLabel={model.accessibilityLabel}
      testID={model.testID}
      className="flex-1 rounded-lg border border-olive bg-paper-up px-3 py-3 gap-1"
    >
      {/* Sizes are fitted to the measured card width (luckBoxSizes): the title
          never wraps, the full name shrinks then wraps rather than truncating,
          and the army swing shrinks to stay beside the score. */}
      <View onLayout={event => setContentWidth(event.nativeEvent.layout.width)} className="gap-1">
        <Text className={`font-stencil uppercase ${tone}`} style={{ fontSize: sizes.title, letterSpacing: sizes.titleLetterSpacing }} numberOfLines={1}>
          {model.title}
        </Text>
        <Text className="font-stencil text-ink" style={{ fontSize: sizes.name }} numberOfLines={2}>{model.name}</Text>
        <View className="flex-row items-baseline gap-1.5">
          <Text className={`font-stencil ${SCORE_TONE[model.scoreTone]}`} style={{ fontSize: sizes.score }}>{model.score}</Text>
          <Text className="font-stencil text-ink" style={{ fontSize: sizes.armies }} numberOfLines={1}>{model.armies}</Text>
        </View>
      </View>
      <Text className="font-typed text-xs text-ink-soft" numberOfLines={2}>{model.detail}</Text>
      <Text className="font-stencil text-[10px] uppercase text-blood mt-1">{model.cta}</Text>
    </TouchableOpacity>
  )
}
